/**
 * Contas de áudio da mistura de microfones (spec 016). Sem navegador aqui:
 * tudo é Float32Array entrando e saindo, pra testar sem microfone.
 */

/** Taxa da mistura. Os microfones que chegam em outra taxa são reamostrados. */
export const MIX_RATE = 48_000;

export function rms(buf: Float32Array): number {
  if (buf.length === 0) return 0;
  let s = 0;
  for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i];
  return Math.sqrt(s / buf.length);
}

export function paraDb(nivel: number): number {
  return nivel > 0 ? 20 * Math.log10(nivel) : -Infinity;
}

export function dbParaGanho(db: number): number {
  return Math.pow(10, db / 20);
}

/**
 * Corte de ruído 0–10 → limiar em dBFS. 0 = sem corte. 3 (padrão) = −54 dB:
 * sala quieta fica abaixo, voz a 1–2 m do microfone passa.
 */
export function corteDb(corte: number): number | null {
  return corte <= 0 ? null : -66 + 4 * Math.min(10, corte);
}

/** Depois de calar, o microfone segue aberto por isto (não corta fim de palavra). */
const SEGURA_S = 0.3;
const ABRE_S = 0.005;
const FECHA_S = 0.12;

/**
 * Corte de ruído (gate) de UM microfone: abaixo do limiar ele se cala, pra
 * o chiado de quem está longe não somar na mistura. Abre rápido, segura
 * 300 ms e fecha em rampa (sem estalo).
 */
export class Gate {
  private ganho = 0;
  private resto = 0;
  private limiar: number | null;
  private readonly coefAbre: number;
  private readonly coefFecha: number;

  constructor(private readonly taxa: number, corte: number) {
    this.limiar = corteDb(corte);
    this.coefAbre = 1 - Math.exp(-1 / (ABRE_S * taxa));
    this.coefFecha = 1 - Math.exp(-1 / (FECHA_S * taxa));
  }

  setCorte(corte: number): void {
    this.limiar = corteDb(corte);
  }

  /** Aplica no bloco (no lugar). Devolve se ficou aberto. */
  process(buf: Float32Array): boolean {
    if (this.limiar === null) return true;
    if (paraDb(rms(buf)) >= this.limiar) this.resto = SEGURA_S * this.taxa;
    else this.resto = Math.max(0, this.resto - buf.length);
    const alvo = this.resto > 0 ? 1 : 0;
    const coef = alvo > this.ganho ? this.coefAbre : this.coefFecha;
    let g = this.ganho;
    for (let i = 0; i < buf.length; i++) {
      g += (alvo - g) * coef;
      buf[i] *= g;
    }
    this.ganho = g;
    return alvo === 1;
  }
}

/** Reamostragem linear contínua (bloco a bloco, sem emenda audível pra voz). */
export class Resampler {
  private pos = 0;
  private ultimo = 0;
  private readonly passo: number;

  constructor(readonly de: number, readonly para: number) {
    this.passo = de / para;
  }

  process(input: Float32Array): Float32Array {
    if (this.de === this.para || input.length === 0) return input;
    const n = input.length;
    const out = new Float32Array(Math.max(0, Math.ceil((n - 1 - this.pos) / this.passo) + 1));
    let k = 0;
    let p = this.pos;
    while (p <= n - 1 && k < out.length) {
      const i = Math.floor(p);
      const f = p - i;
      const a = i < 0 ? this.ultimo : input[i];
      const b = f === 0 ? a : input[i + 1];
      out[k++] = a + (b - a) * f;
      p += this.passo;
    }
    this.pos = p - n;
    this.ultimo = input[n - 1];
    return k === out.length ? out : out.subarray(0, k);
  }
}

/**
 * Fila de amostras de um microfone até o relógio da mistura pedir. Cheia,
 * descarta as mais velhas; vazia, completa com silêncio — cada aparelho tem
 * o próprio relógio e eles escorregam devagar um do outro.
 */
export class Fifo {
  private readonly buf: Float32Array;
  private ini = 0;
  private tam = 0;

  constructor(private readonly cap: number) {
    this.buf = new Float32Array(cap);
  }

  get size(): number {
    return this.tam;
  }

  push(x: Float32Array): void {
    let src = x;
    if (src.length >= this.cap) {
      src = src.subarray(src.length - this.cap);
      this.ini = 0;
      this.tam = 0;
    }
    const sobra = this.tam + src.length - this.cap;
    if (sobra > 0) this.descartar(sobra);
    let fim = (this.ini + this.tam) % this.cap;
    for (let i = 0; i < src.length; i++) {
      this.buf[fim] = src[i];
      fim = fim + 1 === this.cap ? 0 : fim + 1;
    }
    this.tam += src.length;
  }

  /** Tira `n` amostras; o que faltar vem como silêncio. */
  pull(n: number): Float32Array {
    const out = new Float32Array(n);
    const m = Math.min(n, this.tam);
    for (let i = 0; i < m; i++) {
      out[i] = this.buf[this.ini];
      this.ini = this.ini + 1 === this.cap ? 0 : this.ini + 1;
    }
    this.tam -= m;
    return out;
  }

  /** Mantém só as `n` mais novas (segura o atraso quando um aparelho corre na frente). */
  manterUltimas(n: number): void {
    if (this.tam > n) this.descartar(this.tam - n);
  }

  private descartar(n: number): void {
    this.ini = (this.ini + n) % this.cap;
    this.tam -= n;
  }
}

/** Soma `fonte × ganho` em `destino` (mesmo tamanho). */
export function somar(destino: Float32Array, fonte: Float32Array, ganho: number): void {
  const n = Math.min(destino.length, fonte.length);
  for (let i = 0; i < n; i++) destino[i] += fonte[i] * ganho;
}

/** Segura a soma em [−1, 1] (dois microfones altos juntos estourariam). */
export function limitar(buf: Float32Array): void {
  for (let i = 0; i < buf.length; i++) {
    if (buf[i] > 1) buf[i] = 1;
    else if (buf[i] < -1) buf[i] = -1;
  }
}

/** Abaixo disto ninguém está falando perto: o bloco não conta. */
const FALA_DB = -45;
/** Esquerdo e direito com esta diferença = sons diferentes. */
const DIFERENCA_DB = 6;
const BLOCOS_MIN = 30;

/**
 * Descobre se um aparelho de 2 canais manda o MESMO som nos dois (receptor
 * que mistura, microfone comum) ou sons diferentes (receptor que põe cada
 * transmissor num canal). Só conta blocos com fala.
 */
export class DetectorCanais {
  private falados = 0;
  private diferentes = 0;

  push(esq: Float32Array, dir: Float32Array): void {
    const de = paraDb(rms(esq));
    const dd = paraDb(rms(dir));
    if (Math.max(de, dd) < FALA_DB) return;
    this.falados++;
    if (Math.abs(de - dd) >= DIFERENCA_DB) this.diferentes++;
  }

  get resultado(): 'iguais' | 'diferentes' | null {
    if (this.falados < BLOCOS_MIN) return null;
    return this.diferentes / this.falados >= 0.3 ? 'diferentes' : 'iguais';
  }
}
